import { Component, Output, EventEmitter, Input, ChangeDetectionStrategy } from '@angular/core';
import { GenericStudentCardComponent } from '../generic-student-card/generic-student-card.component';
import { EndorsementActionsComponent } from '../student-card-actions/endorsement-actions/endorsement-actions.component';
import { AdminActionsComponent } from '../student-card-actions/admin-actions/admin-actions.component';
import { SchoolRosterStudent } from '@k12/models';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-student-card',
  template: `
    <lib-generic-student-card
      #card
      [editable]="editable"
      [student]="student"
      [schoolTermGrades]="schoolTermGrades"
      [schoolGradeYears]="schoolGradeYears"
      [attendanceTypes]="attendanceTypes"
      [isExpanded]="isExpanded"
      [lockPriorTermCosts]="!showAdminActions"
      [onActionCompleted]="onActionCompleted"
      (toggleExpand)="toggleExpand.emit($event)">
      @if (editable) {
        <lib-endorsement-actions
          studentCardActions
          [editable]="editable"
          [student]="student"
          [schoolEndorsementRestricted]="issueRestrictions?.endorsementRestricted ?? null"
          [form]="card.form"
          [isNotAttending]="card.isNotAttending"
          [individualCostErrors]="card.individualCostErrors"
          (endorsed)="onEndorsed($event)"
          (validateForm)="card.validateIndividualCost()"
          (syncFormToModel)="card.syncFormToModel()"
          (cancelForm)="card.resetForm()"
          (withdrawalRequested)="onWithdrawal($event)">
        </lib-endorsement-actions>
      }
      @if (showAdminActions) {
        <lib-admin-actions
          studentCardActions
          [student]="student"
          [form]="card.form"
          [isNotAttending]="card.isNotAttending"
          [disableRevert]="disableBackwardMove"
          [individualCostErrors]="card.individualCostErrors"
          (saved)="onSaved($event)"
          (reverted)="onReverted($event)"
          (validateForm)="card.validateIndividualCost()"
          (syncFormToModel)="card.syncFormToModel()">
        </lib-admin-actions>
      }
    </lib-generic-student-card>
  `,
  standalone: true,
  imports: [GenericStudentCardComponent, EndorsementActionsComponent, AdminActionsComponent]
})
export class StudentCardComponent extends GenericStudentCardComponent {
  @Input() override editable = false;
  @Input() override isExpanded = false;
  @Input() showAdminActions = false;
  @Input() disableBackwardMove = false;
  @Input() onSavedCallback?: (studentEnrollmentId: string) => void;
  @Input() onRevertedCallback?: (studentEnrollmentId: string) => void;
  @Input() onWithdrawalCallback?: (student: SchoolRosterStudent) => void;
  @Output() endorsed = new EventEmitter<string>();
  @Output() saved = new EventEmitter<string>();
  @Output() reverted = new EventEmitter<string>();
  @Output() override withdrawalRequested = new EventEmitter<SchoolRosterStudent>();

  constructor() {
    super();
  }

  onEndorsed(studentEnrollmentId: string): void {
    this.endorsed.emit(studentEnrollmentId);
    if (this.onEndorsedCallback) {
      this.onEndorsedCallback(studentEnrollmentId);
    }
    if (this.onActionCompleted) {
      this.onActionCompleted(studentEnrollmentId);
    }
  }

  onSaved(studentEnrollmentId: string): void {
    this.saved.emit(studentEnrollmentId);
    if (this.onSavedCallback) {
      this.onSavedCallback(studentEnrollmentId);
    }
  }

  onReverted(studentEnrollmentId: string): void {
    this.reverted.emit(studentEnrollmentId);
    if (this.onRevertedCallback) {
      this.onRevertedCallback(studentEnrollmentId);
    }
    if (this.onActionCompleted) {
      this.onActionCompleted(studentEnrollmentId);
    }
  }

  override onWithdrawal(student: SchoolRosterStudent): void {
    this.withdrawalRequested.emit(student);
    if (this.onWithdrawalCallback) {
      this.onWithdrawalCallback(student);
    }
  }
}
